import { useState, useEffect, useRef } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FiRefreshCw } from "react-icons/fi";

const API_BASE = "https://rentdoc-a0d223e1499e.herokuapp.com/api";

export default function BuildiumUserList({ setDialog }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sigModal, setSigModal] = useState({ open: false, user: null });

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/buildium/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load Buildium users",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSaved = (userId, phone) => {
    setUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, phone } : u)),
    );
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE}/buildium/sync`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setDialog({
        open: true,
        type: "success",
        title: "Sync Complete",
        message: res.data.message || "Buildium users updated successfully",
      });
      await fetchUsers();
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Sync Failed",
        message: err.response?.data?.message || "Unknown error occurred",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSignatureSaved = (userId, signatureUrl, signatureUpdatedAt) => {
    setUsers((prev) =>
      prev.map((u) =>
        u._id === userId ? { ...u, signatureUrl, signatureUpdatedAt } : u,
      ),
    );
  };

  const handleSignatureDeleted = (userId) => {
    setUsers((prev) =>
      prev.map((u) =>
        u._id === userId
          ? { ...u, signatureUrl: null, signatureUpdatedAt: null }
          : u,
      ),
    );
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 text-lg">
        Loading Buildium users...
      </div>
    );

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Buildium Users
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {users.length} user{users.length !== 1 ? "s" : ""} synced
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className={`flex items-center gap-3 px-6 py-3 rounded-2xl text-white font-semibold transition-all ${
            syncing
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl"
          }`}
        >
          <FiRefreshCw className={`w-5 h-5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync from Buildium"}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              {[
                "Name",
                "Email",
                "Role",
                "Status",
                "Signature",
                "Phone",
                "Last Synced",
              ].map((h) => (
                <th
                  key={h}
                  className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-8 py-16 text-center">
                  <div className="text-gray-400 text-xl">
                    No Buildium users yet
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Click "Sync from Buildium" to import
                  </p>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.buildiumId}
                  className="hover:bg-gray-50 transition-colors group"
                >
                  <td className="px-8 py-6">
                    <div className="text-base font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                    {user.companyName && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {user.companyName}
                      </div>
                    )}
                  </td>
                  <td className="px-8 py-6 text-sm text-gray-600">
                    {user.email || "—"}
                  </td>
                  <td className="px-8 py-6">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      {user.userRole?.Name || user.userTypes?.join(", ") || "—"}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                        user.isActive
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-600"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${user.isActive ? "bg-green-500" : "bg-red-400"}`}
                      />
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    {user.signatureUrl ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={user.signatureUrl}
                          alt="Signature"
                          className="h-8 max-w-[80px] object-contain rounded-lg bg-gray-50 border border-gray-100 px-1"
                        />
                        <button
                          onClick={() => setSigModal({ open: true, user })}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSigModal({ open: true, user })}
                        className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 hover:border-indigo-400 hover:text-indigo-600 text-gray-600 text-xs font-semibold rounded-2xl transition-all"
                      >
                        + Add Signature
                      </button>
                    )}
                  </td>

                  <td className="px-8 py-6">
                    <PhoneCell
                      user={user}
                      onSaved={(phone) => handlePhoneSaved(user._id, phone)}
                      setDialog={setDialog}
                    />
                  </td>
                  <td className="px-8 py-6 text-xs text-gray-400">
                    {new Date(user.lastSynced).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {sigModal.open && sigModal.user && (
        <SignatureModal
          open={sigModal.open}
          user={sigModal.user}
          onClose={() => setSigModal({ open: false, user: null })}
          onSaved={handleSignatureSaved}
          onDeleted={handleSignatureDeleted}
          setDialog={setDialog}
        />
      )}
    </div>
  );
}

function SignatureModal({
  open,
  user,
  onClose,
  onSaved,
  onDeleted,
  setDialog,
}) {
  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mode, setMode] = useState("draw");
  const [previewUrl, setPreviewUrl] = useState(user.signatureUrl || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [drawnDataUrl, setDrawnDataUrl] = useState(null);

  const userName =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.companyName ||
    "User";

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setDialog({
        open: true,
        type: "error",
        title: "Invalid File",
        message: "Please select an image file (PNG, JPG, etc.)",
      });
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("token");
    try {
      let res;
      if (mode === "upload" && selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        res = await axios.post(
          `${API_BASE}/buildium/users/${user._id}/signature`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else if (mode === "draw" && drawnDataUrl) {
        res = await axios.post(
          `${API_BASE}/buildium/users/${user._id}/signature`,
          { signatureDataUrl: drawnDataUrl },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else {
        setDialog({
          open: true,
          type: "error",
          title: "No Signature",
          message:
            mode === "draw"
              ? "Please draw a signature first."
              : "Please select an image file first.",
        });
        setSaving(false);
        return;
      }
      if (res.data?.success) {
        onSaved(user._id, res.data.signatureUrl, res.data.signatureUpdatedAt);
        setDialog({
          open: true,
          type: "success",
          title: "Signature Saved",
          message: `Signature for ${userName} has been saved.`,
        });
        onClose();
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Save Failed",
        message: err.response?.data?.message || "Failed to save signature.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.delete(
        `${API_BASE}/buildium/users/${user._id}/signature`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.data?.success) {
        onDeleted(user._id);
        setPreviewUrl(null);
        setDialog({
          open: true,
          type: "success",
          title: "Signature Removed",
          message: `Signature for ${userName} has been removed.`,
        });
        onClose();
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Delete Failed",
        message: err.response?.data?.message || "Failed to remove signature.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-gray-100">
              <div>
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  {user.signatureUrl ? "Edit" : "Add"} Signature
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-0.5">{userName}</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            <div className="px-8 py-6 space-y-6">
              {/* Current signature preview */}
              {user.signatureUrl &&
                !drawnDataUrl &&
                mode === "draw" &&
                !selectedFile && (
                  <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Current Signature
                      </span>
                      {user.signatureUpdatedAt && (
                        <span className="text-xs text-gray-400">
                          Updated{" "}
                          {new Date(user.signatureUpdatedAt).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric", year: "numeric" },
                          )}
                        </span>
                      )}
                    </div>
                    <img
                      src={user.signatureUrl}
                      alt="Current signature"
                      className="max-h-16 mx-auto"
                    />
                  </div>
                )}

              {/* Mode toggle */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "draw", label: "Draw", icon: "✏️" },
                  { key: "upload", label: "Upload Image", icon: "📁" },
                ].map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setMode(key);
                      if (key === "draw") {
                        setSelectedFile(null);
                      }
                      if (key === "upload") {
                        setDrawnDataUrl(null);
                        sigCanvas.current?.clear();
                      }
                    }}
                    className={`py-3 rounded-2xl border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                      mode === key
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <span>{icon}</span> {label}
                  </button>
                ))}
              </div>

              {/* Draw mode */}
              {mode === "draw" && (
                <div>
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl bg-white overflow-hidden">
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      canvasProps={{
                        width: 440,
                        height: 150,
                        className: "w-full",
                        style: { width: "100%", height: "150px" },
                      }}
                      onEnd={() => {
                        if (sigCanvas.current)
                          setDrawnDataUrl(
                            sigCanvas.current.toDataURL("image/png"),
                          );
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      sigCanvas.current?.clear();
                      setDrawnDataUrl(null);
                    }}
                    className="mt-2 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors"
                  >
                    Clear Canvas
                  </button>
                </div>
              )}

              {/* Upload mode */}
              {mode === "upload" && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors text-sm font-medium"
                  >
                    {selectedFile
                      ? selectedFile.name
                      : "Click to select signature image (PNG, JPG)"}
                  </button>
                  {selectedFile && previewUrl && (
                    <div className="mt-4 rounded-2xl bg-gray-50 border border-gray-100 p-4 text-center">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-3">
                        Preview
                      </span>
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-16 mx-auto"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between bg-gray-50 px-8 py-5 border-t border-gray-100 rounded-b-3xl">
              <div>
                {user.signatureUrl && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-sm font-semibold text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                  >
                    {deleting ? "Removing..." : "Remove Signature"}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={
                    saving ||
                    (mode === "draw" && !drawnDataUrl) ||
                    (mode === "upload" && !selectedFile)
                  }
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Save Signature"}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
function PhoneCell({ user, onSaved, setDialog }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  const syncedPhone =
    user.phoneNumbers?.find((p) => p.Type === "Mobile")?.Number ||
    user.phoneNumbers?.find((p) => p.Type === "Home")?.Number ||
    user.phoneNumbers?.[0]?.Number ||
    null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE}/buildium/users/${user._id}/phone`,
        { phone: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved(value.trim() || null);
      setEditing(false);
      setDialog({
        open: true,
        type: "success",
        title: "Phone Updated",
        message: "Phone number saved for N1 forms.",
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Save Failed",
        message: err.response?.data?.message || "Failed to save phone number.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 min-w-[180px]">
        <input
          type="tel"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={syncedPhone || "e.g. 416-555-0100"}
          className="flex-1 bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl py-1.5 px-3 text-sm text-gray-900 placeholder-gray-400 outline-none min-w-0"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") {
              setValue(user.phone || "");
              setEditing(false);
            }
          }}
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
        >
          {saving ? "..." : "Save"}
        </button>
        <button
          onClick={() => {
            setValue(user.phone || "");
            setEditing(false);
          }}
          className="px-3 py-1.5 bg-white border border-gray-200 text-gray-500 text-xs font-semibold rounded-xl hover:bg-gray-50 transition"
        >
          Cancel
        </button>
      </div>
    );
  }

  const displayPhone = user.phone || syncedPhone;

  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-sm ${user.phone ? "text-gray-900 font-medium" : "text-gray-400"}`}
      >
        {displayPhone || "—"}
      </span>
      {user.phone && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-600">
          N1
        </span>
      )}
      <button
        onClick={() => setEditing(true)}
        className="text-xs font-semibold text-gray-400 hover:text-indigo-600 transition-colors opacity-0 group-hover:opacity-100"
      >
        {user.phone ? "Edit" : "+ Add"}
      </button>
    </div>
  );
}
